class CallbackPopupForm {
  constructor(form) {
    this.form = form;
    this.endpoint = form.dataset.endpoint;
    this.pageUrl = form.dataset.pageUrl || window.location.pathname;
    this.submitButton = form.querySelector('[data-callback-submit]');
    this.defaultButtonText = this.submitButton?.textContent?.trim() || '';
    this.messageEl = form.querySelector('[data-callback-message]');
    this.phoneInput = form.querySelector('[data-callback-phone]');
    this.turnstileHost = form.querySelector('[data-callback-turnstile]');
    this.turnstileSitekey = form.dataset.turnstileSitekey || this.turnstileHost?.dataset.sitekey || '';
    this.turnstileWidgetId = null;
    this.turnstileToken = '';

    form.addEventListener('submit', this.onSubmit);
    this.initPhoneMask();
    this.initTurnstile();
  }

  initPhoneMask() {
    if (!this.phoneInput) return;

    this.phoneInput.addEventListener('focus', () => {
      if (!this.phoneInput.value.trim()) {
        this.phoneInput.value = '+48 ';
      }
    });

    this.phoneInput.addEventListener('input', () => {
      const formatted = formatPolishPhone(this.phoneInput.value);
      this.phoneInput.value = formatted;
    });

    this.phoneInput.addEventListener('blur', () => {
      if (this.phoneInput.value.replace(/\D/g, '') === '48') {
        this.phoneInput.value = '';
      }
    });

    this.phoneInput.addEventListener('paste', (event) => {
      event.preventDefault();
      const pasted = event.clipboardData?.getData('text') || '';
      this.phoneInput.value = formatPolishPhone(pasted);
    });
  }

  initTurnstile() {
    if (!this.turnstileSitekey || !this.turnstileHost) return;

    const render = () => {
      if (this.turnstileWidgetId !== null) return;
      if (!window.turnstile?.render) return false;

      this.turnstileWidgetId = window.turnstile.render(this.turnstileHost, {
        sitekey: this.turnstileSitekey,
        theme: 'light',
        callback: (token) => {
          this.turnstileToken = token || '';
        },
        'expired-callback': () => {
          this.turnstileToken = '';
        },
        'error-callback': () => {
          this.turnstileToken = '';
        },
      });
      return true;
    };

    if (render()) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (render() || attempts > 40) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  resetTurnstile() {
    this.turnstileToken = '';
    if (this.turnstileWidgetId !== null && window.turnstile?.reset) {
      window.turnstile.reset(this.turnstileWidgetId);
    }
  }

  onSubmit = async (event) => {
    event.preventDefault();

    if (!this.endpoint) {
      this.showMessage('error', this.form.dataset.errorConfig || 'Form is not configured.');
      return;
    }

    const formData = new FormData(this.form);
    const phoneRaw = String(formData.get('phone') || '').trim();
    const payload = {
      name: String(formData.get('name') || '').trim(),
      phone: formatPolishPhone(phoneRaw),
      email: String(formData.get('email') || '').trim(),
      page: this.pageUrl,
      website: String(formData.get('website') || '').trim(),
      turnstileToken: this.turnstileToken,
    };

    if (!payload.name || !payload.phone || payload.phone.replace(/\D/g, '').length < 11) {
      this.showMessage('error', this.form.dataset.errorRequired || 'Please fill required fields.');
      return;
    }

    if (this.turnstileSitekey && !payload.turnstileToken) {
      this.showMessage('error', this.form.dataset.errorCaptcha || 'Please complete the captcha.');
      return;
    }

    this.setLoading(true);
    this.showMessage('', '');

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        if (response.status === 403 && errorBody.error === 'Captcha failed') {
          throw new Error('captcha');
        }
        if (response.status === 403 && errorBody.error === 'Origin not allowed') {
          throw new Error('origin');
        }
        if (response.status === 400 && errorBody.error === 'Invalid phone') {
          throw new Error('phone');
        }
        if (response.status === 429) {
          throw new Error('rate');
        }
        throw new Error('Request failed');
      }

      this.form.reset();
      this.resetTurnstile();
      this.showMessage('success', this.form.dataset.successText || 'Thank you! We will call you back soon.');
    } catch (error) {
      if (error?.message === 'captcha') {
        this.resetTurnstile();
        this.showMessage('error', this.form.dataset.errorCaptcha || 'Please complete the captcha.');
      } else if (error?.message === 'phone') {
        this.showMessage('error', this.form.dataset.errorPhone || 'Please enter a valid phone number.');
      } else if (error?.message === 'rate') {
        this.showMessage('error', this.form.dataset.errorRate || 'Too many requests. Please try again later.');
      } else if (error?.message === 'origin') {
        this.showMessage('error', this.form.dataset.errorGeneric || 'Something went wrong. Please try again.');
        console.error('Callback form blocked by CORS origin allowlist');
      } else {
        this.showMessage('error', this.form.dataset.errorGeneric || 'Something went wrong. Please try again.');
      }
    } finally {
      this.setLoading(false);
    }
  };

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.setAttribute('aria-busy', String(isLoading));
    this.submitButton.textContent = isLoading
      ? this.form.dataset.loadingText || 'Sending...'
      : this.defaultButtonText;
  }

  showMessage(type, text) {
    if (!this.messageEl) return;

    this.messageEl.hidden = !text;
    this.messageEl.textContent = text;
    this.messageEl.classList.remove('callback-popup__message--success', 'callback-popup__message--error');

    if (type === 'success') {
      this.messageEl.classList.add('callback-popup__message--success');
    }

    if (type === 'error') {
      this.messageEl.classList.add('callback-popup__message--error');
    }
  }
}

/**
 * Normalize to Polish display format: +48 XXX XXX XXX
 * Accepts paste of 512…, 0512…, 48…, +48…, +380… (kept as +380 … loosely).
 */
function formatPolishPhone(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  // Keep non-PL international numbers readable without forcing +48.
  const digitsOnly = raw.replace(/\D/g, '');
  if (raw.startsWith('+') && !digitsOnly.startsWith('48') && !digitsOnly.startsWith('0')) {
    // e.g. +380… — don't force PL mask
    const intl = digitsOnly.slice(0, 15);
    return `+${intl}`;
  }

  let national = digitsOnly;

  if (national.startsWith('48')) {
    national = national.slice(2);
  }
  if (national.startsWith('0')) {
    national = national.slice(1);
  }

  national = national.slice(0, 9);

  const parts = [];
  if (national.length > 0) parts.push(national.slice(0, 3));
  if (national.length > 3) parts.push(national.slice(3, 6));
  if (national.length > 6) parts.push(national.slice(6, 9));

  return parts.length ? `+48 ${parts.join(' ')}` : '+48 ';
}

function initCallbackPopupForms() {
  document.querySelectorAll('[data-callback-form]').forEach((form) => {
    if (form.dataset.callbackInitialized === 'true') return;
    form.dataset.callbackInitialized = 'true';
    new CallbackPopupForm(form);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCallbackPopupForms);
} else {
  initCallbackPopupForms();
}

document.addEventListener('shopify:section:load', initCallbackPopupForms);
